import {useCallback} from 'react'
import {useResetSessionMutation, useViewAsSessionMutation} from '@apis/infrastructureTenants'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useSnackbar} from '@hooks/useSnackbar'
import {getApiErrorMessage} from '@shared/utils/errorHandling'

interface ViewAsRequest {
  organization_id: string
  user_id?: number | string
  team_ids?: string[]
}

interface UseViewAsResult {
  startViewAs: (request: ViewAsRequest) => Promise<void>
  resetViewAs: () => Promise<void>
  isStarting: boolean
  isResetting: boolean
}

/**
 * "View As" session controls. Starting or resetting an impersonation session
 * rewrites the server-side session, so we do a full reload afterwards (mirroring
 * the organization switch) to re-fetch the whole app as the effective identity.
 * The request is either user-targeted ({organization_id, user_id}) or
 * team-targeted ({organization_id, team_ids}).
 */
export const useViewAs = (): UseViewAsResult => {
  const [tCommon] = useCustomTranslation('common')
  const [showNotification] = useSnackbar()
  const [viewAsSession, {isLoading: isStarting}] = useViewAsSessionMutation()
  const [resetSession, {isLoading: isResetting}] = useResetSessionMutation()

  const startViewAs = useCallback(
    async (request: ViewAsRequest): Promise<void> => {
      try {
        await viewAsSession(request).unwrap()
        window.location.href = '/'
      } catch (error) {
        // e.g. an org admin trying to view-as an organization owner (403). Surface
        // the backend's message rather than failing silently, and stay put.
        showNotification({
          message: getApiErrorMessage(error) || tCommon('view_as.start_failed'),
          severity: 'error',
        })
      }
    },
    [viewAsSession, showNotification, tCommon],
  )

  const resetViewAs = useCallback(async (): Promise<void> => {
    try {
      await resetSession().unwrap()
      window.location.href = '/'
    } catch (error) {
      showNotification({
        message: getApiErrorMessage(error) || tCommon('view_as.reset_failed'),
        severity: 'error',
      })
    }
  }, [resetSession, showNotification, tCommon])

  return {startViewAs, resetViewAs, isStarting, isResetting}
}
